/**
 * The schedule read hook (spec 2.1 / 2.1b): fetches the `ScheduleDialogPayload` for
 * either an existing calculation (GET) or a scheduled-but-never-calculated draft
 * (POST), transparently following the over-ceiling `202` async-job envelope by polling
 * its `href` until the identical `200` payload is ready.
 *
 * Mirrors the eval3-new read pattern (react-query `useQuery` over the shared ky `api`
 * with an `AbortSignal`) rather than a module-singleton resource atom — so the
 * abort-on-unmount is React Query's, and there is no cross-mount AbortError trap.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';
import type { ScheduleDialogPayload, ScheduleJobEnvelope } from './types';

/** The draft `configurations` fragment the pre-run POST accepts (identity + blob paths). */
export type ScheduleDraftConfigurations = Record<string, unknown>;

export type SchedulePayloadParams =
  | { mode: 'calc'; calculationId: number }
  | {
      mode: 'draft';
      packageId: number;
      configurations: ScheduleDraftConfigurations;
    };

/** A read that failed because nothing is scheduled yet — drives the schedule-first empty state. */
export class ScheduleNotReadyError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ScheduleNotReadyError';
  }
}

const QUERY_KEY = 'schedule-dialog:payload' as const;
const REQUEST_TIMEOUT_MS = 300_000;
const POLL_INTERVAL_MS = 1_500;
const MAX_POLL_ATTEMPTS = 60;

/**
 * A type guard for the async-job envelope (over-ceiling reads).
 *
 * @param {object} body - The response body (payload or envelope).
 * @return {boolean} True when the body is the async-job envelope.
 */
function isJobEnvelope(
  body: ScheduleDialogPayload | ScheduleJobEnvelope
): body is ScheduleJobEnvelope {
  return (
    typeof (body as ScheduleJobEnvelope).jobId === 'string' &&
    typeof (body as ScheduleJobEnvelope).href === 'string' &&
    !('rows' in body)
  );
}

/**
 * ky requires paths without a leading slash (prefixUrl); the envelope href is absolute.
 *
 * @param {string} href - The absolute href.
 * @return {string} The href without a leading slash.
 */
function stripLeadingSlash(href: string): string {
  return href.replace(/^\//, '');
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Issues the initial read and, if the BE returns the over-ceiling `202` envelope,
 * polls its `href` until the identical `200` payload is ready (or the poll budget is
 * exhausted / the request aborts). A `404` (calc mode) or `422` (draft mode) surfaces
 * as {@link ScheduleNotReadyError} for the schedule-first empty state.
 *
 * @param {SchedulePayloadParams} params - Calc-mode id or draft-mode package + configurations.
 * @param {AbortSignal | undefined} signal - React Query's abort signal.
 * @return {Promise<ScheduleDialogPayload>} The resolved payload.
 */
export async function fetchSchedulePayload(
  params: SchedulePayloadParams,
  signal?: AbortSignal
): Promise<ScheduleDialogPayload> {
  const initial = await requestInitial(params, signal);
  let body = initial;
  let attempts = 0;
  while (isJobEnvelope(body)) {
    if (body.status === 'failed') {
      throw new Error('The schedule computation failed. Please try again.');
    }
    if (attempts >= MAX_POLL_ATTEMPTS) {
      throw new Error('The schedule is taking too long to build.');
    }
    attempts += 1;
    await delay(POLL_INTERVAL_MS);
    body = await api
      .get(stripLeadingSlash(body.href), {
        signal,
        timeout: REQUEST_TIMEOUT_MS,
      })
      .json<ScheduleDialogPayload | ScheduleJobEnvelope>();
  }
  return body;
}

/**
 * The initial read (GET for calc mode, POST for draft mode), mapping the 404/422
 * "nothing scheduled" responses to {@link ScheduleNotReadyError}.
 *
 * @param {SchedulePayloadParams} params - Calc-mode id or draft-mode package + configurations.
 * @param {AbortSignal | undefined} signal - React Query's abort signal.
 * @return {Promise} The payload or the async-job envelope.
 */
async function requestInitial(
  params: SchedulePayloadParams,
  signal?: AbortSignal
): Promise<ScheduleDialogPayload | ScheduleJobEnvelope> {
  try {
    if (params.mode === 'calc') {
      return await api
        .get(`evaluations/calculations/${params.calculationId}/schedule`, {
          signal,
          timeout: REQUEST_TIMEOUT_MS,
        })
        .json<ScheduleDialogPayload | ScheduleJobEnvelope>();
    }
    return await api
      .post(`packages/${params.packageId}/schedule_dialog_payload`, {
        json: { configurations: params.configurations },
        signal,
        timeout: REQUEST_TIMEOUT_MS,
      })
      .json<ScheduleDialogPayload | ScheduleJobEnvelope>();
  } catch (error) {
    if (
      error instanceof HTTPError &&
      (error.response.status === 404 || error.response.status === 422)
    ) {
      throw new ScheduleNotReadyError(
        error.response.status,
        'No schedule exists yet — schedule the scenario first.'
      );
    }
    throw error;
  }
}

/**
 * React Query hook wrapping {@link fetchSchedulePayload}. Disabled until the dialog is
 * open and the identity is present; retries off (a 404/422 is a real empty state, not
 * a transient failure).
 *
 * @param {SchedulePayloadParams} params - Calc-mode id or draft-mode package + configurations.
 * @param {boolean} enabled - Gate (dialog open + schedule complete).
 * @return {object} The query result.
 */
export function useSchedulePayload(
  params: SchedulePayloadParams,
  enabled: boolean
): UseQueryResult<ScheduleDialogPayload, Error> {
  const identity =
    params.mode === 'calc'
      ? params.calculationId
      : { packageId: params.packageId, configurations: params.configurations };
  return useQuery<ScheduleDialogPayload, Error>({
    queryKey: [QUERY_KEY, params.mode, identity],
    queryFn: ({ signal }) => fetchSchedulePayload(params, signal),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}
